import type { AuthScheme, AuthSchemeDependentHook, Webhook } from 'api';
import { AuthSchemeType } from 'api';

import type { DependentHookRow } from './DependentHooksNotice';

import { webhookLabel } from '../../utils/webhookLabel';

export const toggleSchemeTitle = (enabled: boolean) =>
  enabled ? 'Disable key' : 'Enable key';
export const toggleSchemeConfirmLabel = (enabled: boolean) =>
  enabled ? 'Disable' : 'Enable';
export const toggleSchemeSuccess = (enabled: boolean) =>
  enabled ? 'Key disabled' : 'Key enabled';
export const toggleSchemeBody = (enabled: boolean) =>
  enabled
    ? 'Disabling this key immediately breaks API authentication for any integration using it. You can re-enable it from the same menu.'
    : 'Enabling this key restores API authentication for integrations using it.';

export const affectedWebhooksIntro = (count: number) =>
  count === 1
    ? 'Disabling this key will also deactivate the webhook that depends on it:'
    : `Disabling this key will also deactivate the ${count} webhooks that depend on it:`;

export const dependentHookRows = (
  hooks: AuthSchemeDependentHook[],
  webhooks: Webhook[]
): DependentHookRow[] =>
  hooks.map((hook) => {
    const uuid = hook.uuid ?? '';
    const webhook = webhooks.find((candidate) => candidate.uuid === uuid);
    return { uuid, label: webhook ? webhookLabel(webhook) : uuid };
  });
export const failedHooksIntro = (count: number) =>
  count === 1
    ? 'The webhook below could not be deactivated and is still active. Deactivate it manually:'
    : `The ${count} webhooks below could not be deactivated and are still active. Deactivate them manually:`;
export const inactiveHooksIntro = (count: number) =>
  count === 1
    ? 'This webhook uses this key and is still inactive. Enabling a key does not reactivate webhooks, so reactivate it under Developers → Webhooks if it should receive events again:'
    : `These ${count} webhooks use this key and are still inactive. Enabling a key does not reactivate webhooks, so reactivate them under Developers → Webhooks if they should receive events again:`;

export const exportModalTitle = (type: AuthSchemeType) =>
  type === AuthSchemeType.jwt ? 'Export public key' : 'Export certificate';
export const exportPanelLabel = (type: AuthSchemeType) =>
  type === AuthSchemeType.jwt ? 'Public key' : 'PKCS7';
export const exportFilename = (scheme: AuthScheme) => {
  const slug = scheme.name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${slug || 'auth-scheme'}-${
    scheme.type === AuthSchemeType.jwt ? 'public-key' : 'certificate'
  }.pem`;
};
